import React, { useEffect, useRef, useState, useCallback } from "react";
import ExpertCards from "../../Components/ExpertCards/ExpertCards";
import Slider from "react-slick";
import SliderImg from "../../Assets/SliderImg.png";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SliderImgMobile from "../../Assets/SliderImgMobile.png";

import styles from "./index.module.css";
import { Button, Skeleton } from "@mui/material";
import JoinExpert from "../../Components/JoinExpert/JoinExpert";
import HomeDetails from "../../Components/HomeDetails/HomeDetails";
import HomeTabs from "../../Components/HomeTabs/HomeTabs";
import Services from "../../Components/Services/Services";
import ExpertSlider from "../../Components/ExpertSlider/ExpertSlider";
import { open_api_with_cookies } from "../../Utils/network";
import { useLocation } from "react-router-dom";
import ContactUsFrom from "../../Components/ContactUsFrom/ContactUsFrom";
import SliderBase from "../../Components/Slider/SliderBase";
import JoinUsForm from "../../Components/JoinUsForm/JoinUsForm";
import AppPromotion from "../../Components/Card/AppPromotion";
import { Helmet } from "react-helmet";
import SearchBar from "../../Components/Card/SearchBar";
import GeneralChips from "../../Components/Chips/GeneralChips";

// Spinning loader component for infinite scroll
const LoadingSpinner = () => (
  <div className={styles.spinner_container}>
    <div className={styles.spinner}></div>
  </div>
);

// Skeleton for expert cards
const ExpertCardSkeleton = () => (
  <div className={styles.expert_skeleton}>
    <Skeleton height={300} width="100%" style={{ marginBottom: "10px" }} />
  </div>
);

function CustomPrevArrow(props) {
  const { onClick } = props;
  return (
    <button
      className={`${styles.customarrow} ${styles.customprevarrow}`}
      onClick={onClick}
    >
      <ArrowBackIosNewIcon />
    </button>
  );
}

function CustomNextArrow(props) {
  const { onClick } = props;
  return (
    <button
      className={`${styles.customarrow} ${styles.customnextarrow}`}
      onClick={onClick}
    >
      <ArrowForwardIosIcon />
    </button>
  );
}

var settings = {
  className: styles.slickstyles,
  autoplay: true,
  infinite: true,
  slidesToShow: 1,
  slidesToScroll: 1,
  adaptiveHeight: false,
  arrows: true,
  autoplaySpeed: 2000,
  prevArrow: <CustomPrevArrow />,
  nextArrow: <CustomNextArrow />,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        arrows: false,
      },
    },
  ],
};

function ExpertListingPage() {
  const [expertsList, setExpertsList] = useState([]);
  const [filteredExperts, setFilteredExperts] = useState([]);
  const [visibleExperts, setVisibleExperts] = useState([]);
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [maxpage, setmaxPage] = useState(1);
  const [expertCount, setExpertCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChips, setSelectedChips] = useState(["all"]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [error, setError] = useState(null);
  const [expertCategories, setExpertCategories] = useState([]);
  const ITEMS_PER_PAGE = 12;
  const observerRef = useRef(null);

  // Create intersection observer for infinite scrolling
  const lastExpertElementRef = useCallback(
    (node) => {
      if (isLoadingMore) return;

      // Disconnect previous observer if exists
      if (observerRef.current) observerRef.current.disconnect();

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting && page < maxpage) {
            setPage((prevPage) => prevPage + 1);
          }
        },
        {
          threshold: 0.1, // Trigger when at least 10% of the element is visible
          rootMargin: "0px 0px 500px 0px", // Load earlier before reaching the bottom
        }
      );

      // Observe the node if it exists
      if (node) {
        observerRef.current.observe(node);
      }
    },
    [isLoadingMore, maxpage, page]
  );

  useEffect(() => {
    // Cleanup observer on component unmount
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    setExpertsList([]);
    setFilteredExperts([]);
    setVisibleExperts([]);
    setPage(1);
    setmaxPage(1);
    setIsLoading(true);
    setSelectedCategoryId(null);
    setSelectedChips(["all"]);
  }, []);

  useEffect(() => {
    if (page === 1) {
      getExperts(selectedCategoryId, page, true);
    } else if (page <= maxpage) {
      getExperts(selectedCategoryId, page, false);
    }
  }, [page, selectedCategoryId, searchQuery, maxpage]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchExpertCategories();
  }, []);

  const fetchExpertCategories = async () => {
    try {
      const res = await open_api_with_cookies.get(`core/header/expert_type`);
      if (res?.status === 200 || res?.status === 201 || res?.status === 204) {
        const categories = res?.data?.results || [];
        setExpertCategories(categories);
      }
    } catch (error) {
      console.error("Error fetching expert categories:", error);
    }
  };

  const getExperts = (id, page, isReset) => {
    setIsLoading(page === 1);
    setIsLoadingMore(page > 1);

    let url = `/profiles/experts?page=${page}`;
    if (id) {
      url += `&expert_type=${id}`;
    }
    if (searchQuery) url += `&q=${encodeURIComponent(searchQuery)}`;

    open_api_with_cookies
      .get(url)
      .then((res) => {
        if (res?.status == 200 || res?.status == 201 || res?.status == 204) {
          const maxpage = res?.data?.max_pages;
          setmaxPage(maxpage);
          const data = res?.data?.data;
          setExpertCount(res?.data?.total_count || 0);

          if (isReset) {
            setExpertsList(data);
            setFilteredExperts(data);
            setVisibleExperts(data);
          } else {
            setExpertsList((prev) => [...prev, ...data]);
            setFilteredExperts((prev) => [...prev, ...data]);
            setVisibleExperts((prev) => [...prev, ...data]);
          }
          setError(null);
        }
      })
      .catch((err) => {
        console.log(err);
        setError("Failed to load experts. Please try again later.");
      })
      .finally(() => {
        setIsLoading(false);
        setIsLoadingMore(false);
      });
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setPage(1);
    setTimeout(() => getExperts(selectedCategoryId, 1, true), 0);
  };

  const handleChipClick = (event, chipValue, categoryId = null) => {
    if (chipValue === "all") {
      setSelectedChips(["all"]);
      setSelectedCategoryId(null);
    } else {
      setSelectedChips([chipValue]);
      setSelectedCategoryId(categoryId);
    }

    setPage(1);
    setTimeout(() => getExperts(categoryId, 1, true), 0);
  };

  useEffect(() => {}, [visibleExperts, maxpage]);

  return (
    <>
      <Helmet>
        <title>MyBurgo - Parenting App | Pregnancy App | SelfCare App</title>
        <meta
          name="description"
          content="MyBurgo is India's comprehensive parenting app offering expert guidance, interactive workshops, personalized resources, and community support for parents and pregnant women. Empower your parenting journey with child development tools, curated recipes, mental health support, and more."
        />
      </Helmet>
      <section>
        <SliderBase page={"expert"} />
      </section>

      <section className={styles.expertListingWrapper}>
        <div className={styles.ourExperts}>
          <h1>Our Health Care Experts</h1>

          <SearchBar
            onSearch={handleSearch}
            text="Search for experts by name or specialty"
          />

          <div className={styles.buttons}>
            <GeneralChips
              key="all"
              title="All"
              handleClick={(event) => handleChipClick(event, "all")}
              selectedChips={selectedChips.includes("all") ? ["All"] : []}
            />
            {expertCategories.map((category) => (
              <GeneralChips
                key={category.id}
                title={category.title}
                handleClick={(event) =>
                  handleChipClick(event, "category_" + category.id, category.id)
                }
                selectedChips={
                  selectedChips.includes("category_" + category.id)
                    ? [category.title]
                    : []
                }
              />
            ))}
          </div>
        </div>
      </section>

      {error && (
        <p className={styles.error_message} role="alert">
          {error}
        </p>
      )}

      {isLoading ? (
        <section className={styles.expertCards}>
          {Array(6)
            .fill(0)
            .map((_, index) => (
              <div key={index} className={styles.expertCardContainer}>
                <ExpertCardSkeleton />
              </div>
            ))}
        </section>
      ) : visibleExperts.length > 0 ? (
        <section className={styles.expertCards}>
          {visibleExperts.map((items, index) => {
            // Check if this is the last item
            const isLastItem = index === visibleExperts.length - 1;

            // For debugging
            if (isLastItem) {
              //console.log("Rendering last expert card with ref", items.id);
            }

            return (
              <ExpertCards
                key={items.id || index}
                data={items}
                ref={isLastItem ? lastExpertElementRef : null}
              />
            );
          })}
          {isLoadingMore && <LoadingSpinner />}
          {page < maxpage && !isLoadingMore && (
            <div ref={lastExpertElementRef} className={styles.loadMoreTrigger}>
              {/* Invisible element that also triggers loading */}
            </div>
          )}
        </section>
      ) : (
        <div className={styles.noResults}>
          <p>No experts found matching your criteria.</p>
          <button
            className={styles.resetButton}
            onClick={() => {
              setSelectedChips(["all"]);
              setSelectedCategoryId(null);
              setSearchQuery("");
              getExperts(null, 1, true);
            }}
          >
            Reset filters
          </button>
        </div>
      )}

      <AppPromotion />
      {/* <JoinExpert /> */}
      <JoinUsForm />
    </>
  );
}

export default ExpertListingPage;

const data = [
  { img: SliderImg, id: 1, SliderImgMobile },
  { img: SliderImg, id: 2, SliderImgMobile },
  { img: SliderImg, id: 3, SliderImgMobile },
];
